/**
 * Adapter to convert a Pharmacy First PGD + selected dose band into
 * the format expected by the existing PrescriptionModal component.
 *
 * Bridges the new framework configs to the existing prescription system.
 */

import { getPgd, getDrug, resolveDoseBand, calculateDose } from "@/lib/pharmacyFirst";
import type { PatientInput, SupplyDetails } from "@/lib/pharmacyFirst";
import type { Formulation } from "@/lib/pharmacyFirst";

export interface PrescriptionPrefillItem {
  medication: string;
  dosage: string;
  quantity: string;
  notes?: string;
  prescribedItemId?: string;
}

export interface AllowedMedication {
  name: string;
  dosage: string;
  quantity: string;
  notes?: string;
  contraindications?: string[];
  warnings?: string[];
}

function formulationLabel(drugName: string, formulation: Formulation): string {
  const form = formulation.type.replace(/_/g, " ");
  if (formulation.strength !== undefined && formulation.strengthUnit) {
    return `${drugName} ${formulation.strength}${formulation.strengthUnit} ${form}`;
  }
  return `${drugName} ${form}`;
}

/**
 * Substitute {dose} / {volume} placeholders in an instruction template.
 * - {dose} → computed dose in mg (from band.mgPerKg × patient.weightKg)
 * - {volume} → volume in mL for liquid formulations (dose ÷ strength × volumePer)
 */
function renderInstructionTemplate(
  template: string,
  doseMg: number | string,
  formulation: Formulation,
): string {
  if (!template.includes("{")) return template;
  let out = template.replace(/\{dose\}/g, String(doseMg));
  if (template.includes("{volume}")) {
    const volPer = formulation.volumePer;
    const strength = formulation.strength;
    if (typeof doseMg === "number" && volPer && strength) {
      const mL = Math.round((doseMg / strength) * volPer * 100) / 100;
      out = out.replace(/\{volume\}/g, `${mL}mL`);
    } else {
      out = out.replace(/\{volume\}/g, "—mL");
    }
  }
  return out;
}

/**
 * Convert selected PGD into prefill items for PrescriptionModal
 * Returns an array of prescription items ready to populate the modal
 */
export function buildPrefillItemsFromPgd(
  pgdId: string,
  patient: PatientInput,
  doseStepId?: string,
): PrescriptionPrefillItem[] {
  const pgd = getPgd(pgdId);
  const drug = getDrug(pgd.drugRef);
  const doseBand = resolveDoseBand(pgd, patient, doseStepId);

  if (!doseBand) {
    return [];
  }

  // Preferred = first formulation of the resolved dose band
  const preferredChoice = doseBand.formulations[0];
  const preferredFormulation = drug.formulations.find(
    (f) => f.id === preferredChoice.formulationId,
  );
  if (!preferredFormulation) {
    throw new Error(
      `Formulation ${preferredChoice.formulationId} not found in drug ${drug.id}`,
    );
  }
  const preferredLabel = formulationLabel(drug.name, preferredFormulation);
  const doseMg = calculateDose(doseBand, patient);

  return [
    {
      medication: preferredLabel,
      dosage: renderInstructionTemplate(
        preferredChoice.instruction,
        doseMg,
        preferredFormulation,
      ),
      quantity: preferredChoice.quantity.label,
      notes: "",
      prescribedItemId: preferredFormulation.prescribedItemId || "",
    },
  ];
}

/**
 * Build the list of PGD-permitted medications the pharmacist can choose from.
 * Each entry is a fully-described option (label + dosage + quantity)
 * so picking one repopulates the prescription item.
 */
export function buildAllowedMedicationsFromPgd(
  pgdId: string,
  patient: PatientInput,
  doseStepId?: string,
): AllowedMedication[] {
  const pgd = getPgd(pgdId);
  const drug = getDrug(pgd.drugRef);
  const doseBand = resolveDoseBand(pgd, patient, doseStepId);
  if (!doseBand) return [];

  const seen = new Set<string>();
  const options: AllowedMedication[] = [];
  const doseMg = calculateDose(doseBand, patient);

  for (const choice of doseBand.formulations) {
    const formulation = drug.formulations.find(
      (f) => f.id === choice.formulationId,
    );
    if (!formulation) continue;
    const label = formulationLabel(drug.name, formulation);
    if (seen.has(label)) continue;
    seen.add(label);
    options.push({
      name: label,
      dosage: renderInstructionTemplate(choice.instruction, doseMg, formulation),
      quantity: choice.quantity.label,
    });
  }

  return options;
}

/**
 * Build SupplyDetails for the audit record from the selected PGD + patient
 */
export function buildSupplyDetailsFromPgd(
  pgdId: string,
  patient: PatientInput,
  selectedFormulationId?: string,
  sugarFreeSelected: boolean = false,
  doseStepId?: string,
): SupplyDetails {
  const pgd = getPgd(pgdId);
  const drug = getDrug(pgd.drugRef);
  const doseBand = resolveDoseBand(pgd, patient, doseStepId);

  if (!doseBand) {
    throw new Error(`No dose band found for PGD ${pgdId} — weight may not yet be captured`);
  }

  // Use the first formulation in the band by default, or the one selected
  const formChoice =
    doseBand.formulations.find(
      (f) => f.formulationId === selectedFormulationId
    ) || doseBand.formulations[0];

  const dose = calculateDose(doseBand, patient);
  const formulation = drug.formulations.find((f) => f.id === formChoice.formulationId);
  const instructionText = formulation
    ? renderInstructionTemplate(formChoice.instruction, dose, formulation)
    : formChoice.instruction;

  return {
    drugId: drug.id,
    pgdId: pgd.id,
    pgdVersion: pgd.version,
    formulationId: formChoice.formulationId,
    doseBandId: doseBand.id,
    resolvedDoseMg: dose,
    instructionText,
    quantity: formChoice.quantity,
    sugarFreeSelected,
    totalDurationDays: pgd.duration.days,
  };
}
